import LoadingButton from "@mui/lab/LoadingButton";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { useFormik } from "formik";
import moment from "moment";
import { useEffect, useMemo, useState } from 'react';
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";

import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Div from "@jumbo/shared/Div";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { getVendorName, VendorNameCleanUp } from 'app/redux/actions/subTaskAction';
import { docBaseUrl, hraxios } from "app/services/config";
import subTaskService from "app/services/subtask_service";
import DndWrapper from "./DndWrapper";
import { useQuery } from "react-query";


// ✅ Validation schema moved outside
const validationSchema = yup.object({
    expenseAmount: yup.number()
        .typeError('Expense amount must be a number')
        .required('Expense amount is required')
        .test(
            'is-decimal',
            'Invalid format. Please enter a valid number',
            value => (value === undefined || value === null ? true : /^(\d+)?(\.\d{1,2})?$/.test(value.toString()))
        ),
    vendorId: yup.string().nullable().required('Vendor is required'),
    files: yup.array().min(1, 'At least one file is required'),
    expenseDate: yup.string().required('Expense Date is required'),
    expenseDescription: yup.string().required('Description is required'),
    invoiceNumber: yup.string().required('Invoice No. is required'),
    company: yup
        .array()
        .min(1, "Please select at least one company")
        .of(yup.string())
        .required("Company is required"),
    projects: yup
        .array()
        .min(1, "Please select at least one project")
        .of(yup.string())
        .required("Project is required"),
});


const OperationalExpenseForm = ({ expanseId }) => {
    const Swal = useSwalWrapper();
    const { hideDialog } = useJumboDialog();
    const { setAuthToken } = useJumboAuth();
    const dispatch = useDispatch();
    const { vendorNameData } = useSelector(state => state.typereducer);

    const [files, setFiles] = useState([]);
    const [expenseDoc, setExpenseDoc] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expenseData, setExpenseData] = useState({});
    const [searchVendorName, setSearchVendorName] = useState('');

    // ✅ Fetch vendors
    useEffect(() => {
        subTaskService.getVendorName(0, 30, '')
            .then((data) => {
                dispatch(getVendorName(data?.data?.data));
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    setAuthToken(null);
                }
            });
        return () => {
            dispatch(VendorNameCleanUp());
        };
    }, [dispatch, setAuthToken]);

    // ✅ Fetch expense by id if editing
    useEffect(() => {
        if (expanseId) {
            subTaskService.getOpExpenseDataById(expanseId)
                .then((data) => {
                    setExpenseData(data?.data?.data);
                    setExpenseDoc(data?.data?.data?.bill_reciept_documents || []);
                })
                .catch((err) => {
                    if (err?.response?.status === 401) {
                        setAuthToken(null);
                    }
                });
        }
    }, [expanseId, setAuthToken]);


    // ✅ Dropzone config
    const { getRootProps, getInputProps } = useDropzone({
        accept: 'image/*,application/pdf',
        onDrop: (acceptedFiles) => handleFileUpload(acceptedFiles),
    });


    const { data: projectsList } = useQuery({
        queryKey: ["products-list"],
        queryFn: async () => {
            const response = await hraxios.get(
                "custom_master_data/by_name?name=Projects"
            );
            return response.data.data.data?.custom_list || [];
        },
    });

    const { data: companyList } = useQuery({
        queryKey: ["company-list"],
        queryFn: async () => {
            const response = await hraxios.get("company");
            const companyData = response.data.data.data[0] || [];
            return companyData?.data?.map((item) => item.code) || [];
        },
    });

    // ✅ useFormik
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vendorId: expenseData?.vendor_id?._id || '',
            expenseAmount: expenseData?.expense_amount || '',
            expenseDate: expenseData?.expense_date ? moment(expenseData.expense_date).format('YYYY-MM-DD') : '',
            expenseDescription: expenseData?.expense_description || '',
            expenseNote: expenseData?.expense_note || '',
            gstType: expenseData?.gst_type || '',
            invoiceNumber: expenseData?.invoice_number || '',
            bill_reciept_documents: expenseData?.bill_reciept_documents || [],
            company: expenseData?.company || "",
            projects: expenseData?.projects || "",
        },
        validationSchema,
        onSubmit: async (values) => {
            if (expenseDoc.length === 0 &&
                vendorNameData?.find(v => v.id === values?.vendorId)?.vendor_type !== "rent") {
                return;
            }

            const payload = {
                vendor_id: values.vendorId,
                expense_description: values.expenseDescription,
                expense_amount: values.expenseAmount,
                expense_date: values.expenseDate,
                bill_reciept_documents: expenseDoc,
                status: "Requested",
                expense_note: values.expenseNote,
                invoice_number: values.invoiceNumber,
                gst_type: values.gstType,
                company: values?.company,
                projects: values?.projects,
            };

            try {
                if (expanseId) {
                    await subTaskService.updateOpExpenseStatus(payload, expanseId);
                } else {
                    await subTaskService.addOpExpensePostApi(payload);
                }
                hideDialog();
                Swal.fire({ icon: "success", title: "Success", text: "Expense saved successfully!" })
                    .then(() => window.location.reload());
            } catch (err) {
                hideDialog();
                Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" });
            }
        }
    });

    const handleFileUpload = async (acceptedFiles) => {
        setFiles(pre => [...pre, ...acceptedFiles.map(file => Object.assign(file, { preview: URL.createObjectURL(file) }))]);

        let uploadedDocs = [];
        setIsLoading(true);

        for (let file of acceptedFiles) {
            let formData = new FormData();
            formData.append("attachment", file);
            try {
                const response = await hraxios.post(`operational_expenses/op-expenses-upload`, formData);
                if (response.status === 200) {
                    uploadedDocs.push({
                        key: response?.data?.data?.id?.key,
                        base_url: response?.data?.data?.id?.base_url,
                    });
                }
            } catch (err) {
                Swal.fire({ icon: "error", title: "Upload failed", text: "Something went wrong" });
            }
        }
        setExpenseDoc(prev => [...prev, ...uploadedDocs]);
        setIsLoading(false);
    };

    // ✅ Memoize thumbs
    const thumbs = useMemo(() =>
        files.map(file => (
            <div style={{ display: 'block', border: '1px solid #eaeaea', margin: 4, width: 100, height: 100 }} key={file.name}>
                <img src={file.preview} style={{ width: '100%', height: '100%', objectFit: "cover" }} alt="" />
            </div>
        ))
        , [files]);

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Div sx={{ '& .MuiTextField-root': { mb: 3 } }}>

                {/* Expense Amount */}
                <TextField
                    fullWidth
                    type="number"
                    label="Expense Amount"
                    {...formik.getFieldProps('expenseAmount')}
                    error={formik.touched.expenseAmount && Boolean(formik.errors.expenseAmount)}
                    helperText={formik.touched.expenseAmount && formik.errors.expenseAmount}
                />

                {/* Vendor */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Vendor</InputLabel>
                    <Select
                        name="vendorId"
                        value={formik.values.vendorId}
                        onChange={formik.handleChange}
                        onClose={() => setSearchVendorName('')}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 400,
                                    maxWidth: 400,
                                },
                            },
                        }}
                        sx={{ position: "relative" }}
                    >
                        <MenuItem sx={{
                            background: "#fff", position: "sticky", top: 0, zIndex: 1, ":hover": {
                                background: "#fff",
                            },
                            "&.Mui-selected": {
                                background: "transparent",
                            },

                        }} onMouseDown={(e) => e.stopPropagation()}>
                            <TextField
                                fullWidth
                                value={searchVendorName}
                                placeholder="Search Vendor Name..."
                                variant="outlined"
                                sx={{ marginBottom: '8px', padding: '8px' }}
                                onChange={(e) => {
                                    setSearchVendorName(e.target.value)
                                }}
                                autoComplete="off"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                onKeyUp={(e) => e.stopPropagation()}
                            />
                        </MenuItem>
                        {vendorNameData
                            .filter(v => v?.vendor_name?.toLowerCase().includes(searchVendorName.toLowerCase()))
                            .map(v => (
                                <MenuItem key={v.id} value={v.id}>{v.vendor_name}</MenuItem>
                            ))}
                    </Select>
                    {formik.touched.vendorId && formik.errors.vendorId &&
                        <Typography color="error">{formik.errors.vendorId}</Typography>}
                </FormControl>

                {/* GST */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>GST Tax Type</InputLabel>
                    <Select
                        name="gstType"
                        value={formik.values.gstType}
                        onChange={formik.handleChange}
                    >
                        <MenuItem value="intra_state">Intra-state</MenuItem>
                        <MenuItem value="inter_state">Inter-state</MenuItem>
                    </Select>
                </FormControl>

                {/* Invoice Number */}
                <TextField
                    fullWidth
                    label="Invoice Number"
                    {...formik.getFieldProps('invoiceNumber')}
                    error={formik.touched.invoiceNumber && Boolean(formik.errors.invoiceNumber)}
                    helperText={formik.touched.invoiceNumber && formik.errors.invoiceNumber}
                />

                {/* Expense Date */}
                <TextField
                    fullWidth
                    type="date"
                    label="Expense Date"
                    {...formik.getFieldProps('expenseDate')}
                    InputLabelProps={{ shrink: true }}
                    error={formik.touched.expenseDate && Boolean(formik.errors.expenseDate)}
                    helperText={formik.touched.expenseDate && formik.errors.expenseDate}
                />

                <FormControl sx={{ mt: 1, mb: 3 }} fullWidth>
                    <InputLabel>Company</InputLabel>
                    <Select
                        name="company"
                        multiple={false}
                        value={formik.values?.company || []}
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(e) => {
                            const selectedValues = Array.isArray(e.target.value)
                                ? e.target.value
                                : [e.target.value];
                            formik.setFieldValue("company", selectedValues);
                        }}
                    >
                        {companyList?.map((company) => (
                            <MenuItem key={company} value={company}>
                                {company}
                            </MenuItem>
                        ))}
                    </Select>
                    {formik.touched.company && formik.errors.company &&
                        <Typography color="error">{formik.errors.company}</Typography>}
                </FormControl>

                <FormControl sx={{ mt: 1, mb: 3 }} fullWidth>
                    <InputLabel>Projects</InputLabel>
                    <Select
                        name="projects"
                        multiple={false}
                        value={formik.values?.projects || []}
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(e) => {
                            formik.setFieldValue("projects", [].concat(e.target.value));
                        }}
                    >
                        {projectsList?.map((project) => (
                            <MenuItem key={project} value={project}>
                                {project}
                            </MenuItem>
                        ))}
                    </Select>
                    {formik.touched.projects && formik.errors.projects &&
                        <Typography color="error">{formik.errors.projects}</Typography>}
                </FormControl>
                {/* Description */}
                <TextField
                    fullWidth
                    multiline
                    label="Description"
                    {...formik.getFieldProps('expenseDescription')}
                    error={formik.touched.expenseDescription && Boolean(formik.errors.expenseDescription)}
                    helperText={formik.touched.expenseDescription && formik.errors.expenseDescription}
                />

                {/* Note */}
                <TextField
                    fullWidth
                    multiline
                    label="Note"
                    {...formik.getFieldProps('expenseNote')}
                />

                {/* File Upload */}
                <FormControl>
                    <div {...getRootProps()}>
                        <DndWrapper sx={{ mb: 0 }}>
                            <input {...getInputProps()} />
                            <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center' }}>
                                Drag & drop files here, or click to select
                            </Typography>
                        </DndWrapper>
                    </div>

                    {/* Show newly dropped files */}
                    <aside style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
                        {thumbs}
                    </aside>

                    {/* Show already uploaded documents */}
                    <aside style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
                        {formik.values.bill_reciept_documents.map((doc, i) => {
                            const fileUrl = `${docBaseUrl}${doc.base_url}/${doc.key}`;
                            const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.key);

                            return (
                                <div key={i} style={{ textAlign: "center" }}>
                                    {isImage ? (
                                        <img
                                            src={fileUrl}
                                            alt={doc.key}
                                            style={{
                                                width: "80px",
                                                height: "80px",
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                                border: "1px solid #ddd"
                                            }}
                                        />
                                    ) : (
                                        <a href={fileUrl} target="_blank" rel="noreferrer">
                                            <Typography variant="body2" color="primary">
                                                {doc.key}
                                            </Typography>
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </aside>

                    {formik.dirty && expenseDoc?.length === 0 && (
                        <Typography color="error">
                            Attachment is required
                        </Typography>
                    )}

                </FormControl>
                {/* Save Button */}
                <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 3 }}
                    disabled={isLoading}
                    loading={formik.isSubmitting}
                >
                    {formik.isSubmitting ? "Uploading..." : "Save"}
                </LoadingButton>
            </Div>
        </form>
    );
};

export default OperationalExpenseForm;
